import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import MaterialTable from 'material-table';
import { ThemeProvider, createTheme } from '@mui/material';
import moment from 'moment';
import Spinner from 'components/Spinner';
import ShowToast from 'components/ShowToast';
import { TYPE_FAIL, INTERVAL } from 'assets/constants/Constants';
import { getPoList } from 'lib/axiosUtils';
import { FormatListNumberedIcon, EYE_ICON, EDIT_ICON } from 'assets/constants/Icons';
import '../BOM/bom.css';

const PoListPage = () => {
    const history = useHistory();
    const mytheme = createTheme({});

    const [poList, setPoList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedPo, setSelectedPo] = useState(null);

    const fetchPoList = async () => {
        setIsLoading(true);

        try {
            const response = await getPoList({}, {});
            const data = response?.data?.result || [];

            const mappedData = data.map((po, index) => {
                const enquiry = po.enquiry_id || po.enquiry || {};

                return {
                    ...po,
                    index: index + 1,
                    poId: po._id,
                    poNumber: po.po_number || '-',
                    enquiryId: enquiry?._id || po.enquiry_id || '',
                    enquiryNumber:
                        enquiry?.enquiryNumber ||
                        enquiry?.enquiry_number ||
                        '-',
                    customerName:
                        enquiry?.customer?.customer_name ||
                        enquiry?.customer?.name ||
                        '-',
                    poDate: po.po_date ? moment(po.po_date).format('DD-MM-YYYY') : '-',
                };
            });

            setPoList(mappedData);
        } catch (error) {
            console.error('Error fetching PO list:', error);
            ShowToast('Failed to fetch PO list. Please try again.', TYPE_FAIL, INTERVAL);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPoList();
    }, []);

    const handleViewPo = (po) => {
        setSelectedPo(po);
        setViewModalOpen(true);
    };

    const handleCloseViewModal = () => {
        setViewModalOpen(false);
        setSelectedPo(null);
    };

    const renderViewModal = () => {
        if (!viewModalOpen || !selectedPo) return null;

        return (
            <div className="custom_modal">
                <div className="modal_body">
                    <button
                        type="button"
                        style={{ fontSize: 25 }}
                        className="close text-right"
                        onClick={handleCloseViewModal}
                    >
                        &times;
                    </button>

                    <h5 className="mb-3">
                        <b>PO Details</b>
                    </h5>

                    <hr />

                    <div
                        style={{
                            background: '#f8f9fa',
                            padding: '10px',
                            borderRadius: '4px',
                            border: '1px solid #dee2e6',
                            margin: '14px',
                        }}
                    >
                        <p>
                            <b>PO Number:</b> {selectedPo.poNumber || '-'}
                        </p>

                        <p>
                            <b>Enquiry Number:</b> {selectedPo.enquiryNumber || '-'}
                        </p>

                        <p>
                            <b>PO Date:</b> {selectedPo.poDate || '-'}
                        </p>

                        <p>
                            <b>PO Value:</b> {selectedPo.po_value || '-'}
                        </p>

                        <p>
                            <b>Payment Terms:</b> {selectedPo.payment_terms || '-'}
                        </p>

                        <p>
                            <b>Delivery Terms:</b> {selectedPo.delivery_terms || '-'}
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    const columns = useMemo(
        () => [
            {
                title: 'PO No',
                field: 'poNumber',
                sorting: true,
                filtering: true,
                cellStyle: {
                    fontSize: 12,
                    color: '#2d3d42',
                    fontWeight: 'bold',
                },
            },
            {
                title: 'Enquiry No',
                field: 'enquiryNumber',
                sorting: true,
                filtering: true,
                cellStyle: {
                    fontSize: 12,
                    color: '#2d3d42',
                    fontWeight: 'bold',
                },
            },
            {
                title: 'Customer',
                field: 'customerName',
                sorting: true,
                filtering: true,
                cellStyle: {
                    fontSize: 12,
                    color: '#2d3d42',
                    fontWeight: 'bold',
                },
            },
            {
                title: 'Date',
                field: 'poDate',
                sorting: true,
                filtering: true,
                cellStyle: {
                    fontSize: 12,
                    color: '#2d3d42',
                    fontWeight: 'bold',
                },
            },
        ],
        []
    );

    const actions = [
        {
            icon: () => <i className={`${EYE_ICON}`} title="View PO"></i>,
            tooltip: 'View PO',
            onClick: (event, rowData) => handleViewPo(rowData),
        },
        {
            icon: () => <i className={`${EDIT_ICON}`} title="Edit PO"></i>,
            tooltip: 'Edit PO',
            onClick: (event, rowData) => {
                if (rowData.enquiryId) {
                    history.push(`/create-po/${rowData.enquiryId}`);
                }
            },
        },
    ];

    const tableOptions = {
        actionsColumnIndex: -1,
        search: true,
        filtering: true,
        sorting: true,
        pageSize: 5,
        pageSizeOptions: [5, 10, 20, 50],
        exportButton: true,
        headerStyle: {
            color: '#2d3d42',
            fontWeight: 'bolder',
            fontSize: 14,
        },
        cellStyle: {
            color: '#2d3d42',
            fontSize: 12,
        },
    };

    if (isLoading) return <Spinner />;

    return (
        <div className="main-content">
            <div
                className="pagetitle row"
                style={{ color: 'black', paddingTop: 15, marginTop: '-25px' }}
            >
                <div
                    className="col-md-10 sm_device"
                    style={{ display: 'flex', alignItems: 'center' }}
                >
                    <FormatListNumberedIcon />
                    <span style={{ marginLeft: '7px', marginRight: '10px' }}>
                        Purchase Orders
                    </span>
                </div>

                <div className="col-md-2 sm_device" style={{ textAlign: 'right' }}>
                    <i className="fa fa-calendar"></i> {moment().format('DD MMM YYYY')}
                </div>
            </div>

            <div
                style={{
                    borderBottom: '1px solid lightgray',
                    marginBottom: 15,
                    marginTop: 10,
                }}
            />

            <div className="list-wrapper">
                <div
                    className="mt-4 border list-table-div project-list-table-div"
                    style={{ marginBottom: 50 }}
                >
                    <ThemeProvider theme={mytheme}>
                        <MaterialTable
                            title=""
                            columns={columns}
                            data={poList}
                            actions={actions}
                            options={tableOptions}
                        />
                    </ThemeProvider>

                    {renderViewModal()}
                </div>
            </div>
        </div>
    );
};

export default PoListPage;